// 사용자 유형(userType)별 화면 접근 허용 목록.
//
// 여기 한 곳에만 두는 이유 — 같은 뜻의 조건이 라우트 가드(src/App.js)와
// 사이드바 메뉴(src/components/Sidebar.js) 두 곳에 필요한데, 각자 적어 두면
// 한쪽만 고쳐져 「메뉴에는 없는데 URL 로는 들어가진다」(또는 그 반대)가 난다.

/**
 * v2 화면 — 학습(/students-hub) · 문제(/problem-hub) · 시험지(/exam-papers) — 을 쓸 수 있는 유형.
 *
 * 교사(teacher)를 여는 이유: 공유받은 폴더를 보려면 그 화면에 들어올 수 있어야 한다.
 * 폴더 공유는 받는 쪽이 화면 밖에 있으면 성립하지 않는다.
 *
 * 허용 유형을 「열거」로 판정하는 이유: `userType !== 'teacher'` 로 쓰면 세 번째 유형이
 * 생기는 날 그 값이 아무도 결정하지 않은 채 통과한다. v2 화면은 지금까지 차단이 기본이었으니,
 * 새 유형은 여기에 이름을 올려야만 들어온다.
 */
export const V2_ROLES = ['manager', 'teacher'];

/**
 * OCR(/ocr-hub)을 쓸 수 있는 유형 — 매니저만 남긴다(오너 결정 2026-09-09).
 * v2 화면 셋을 교사에게 열면서 OCR 만 닫아 둔 것이라, V2_ROLES 와 따로 둔다.
 * 값이 하나뿐이어도 배열인 이유는 위와 같다 — 새 유형은 이름을 올려야만 통과한다.
 */
export const OCR_ROLES = ['manager'];

/**
 * **LLM 비용이 나는 실행**을 쓸 수 있는 유형 — 매니저만(오너 지시 2026-09-09).
 *
 * 이 상수가 가르는 것은 「AI 복사」 한 기능이 아니라 호출할 때마다 모델 비용이 나가는 실행 전부다 —
 * 시험지 AI 복사, 문제 분석 요청, 변형 만들기, 문제 만들기. 교사 수만큼 곱해지는 자리라 화면에서 없앤다.
 *
 * 서버는 이 경로들을 교사에게 **열어 뒀다**(BE-20260910 회신 §1-1 「AI 실행 경로 포함」, §5-①).
 * 닫지 않은 것은 오너가 나중에 교사에게 열 예정이기 때문이고 — 그래서 여는 날 고칠 자리는 이 상수 한 줄이다.
 *
 * 이름을 「AI 복사」에서 「AI 실행」으로 넓힌 이유: 분석·변형·생성용 상수를 새로 만들면 값이 같고 뜻이 같은 상수가
 * 둘이 되어, 여는 날 한쪽만 고쳐진다 — 이 파일 머리 주석이 경계하는 바로 그것이다.
 *
 * 감추는 방식 — 시험지 목록 머리의 「진행중인 AI변형 시험지」 줄과 행마다 붙는 [AI변형] 버튼은
 * **부모가 콜백을 넘기지 않으면 그려지지 않는** 구조라(PaperTable·PaperCardList·PaperListPanel)
 * 부품은 손대지 않고 넘기는 자리에서 가른다.
 *
 * 배치 화면 경로(`/exam-papers/batch/:id`)도 같은 값으로 막는다 — 버튼만 감추고 주소를 열어 두면
 * 「메뉴에 없는데 URL 로는 들어가진다」가 되어 화면이 거짓을 말한다(v2 게이팅과 같은 원칙).
 */
export const AI_RUN_ROLES = ['manager'];

/**
 * **문제은행을 확정하는 쓰기**를 할 수 있는 유형 — 매니저만.
 * 서버가 교사에게 403 으로 남긴 경로 넷이 이 축이다(BE-20260910 회신 §1-2):
 * 분석 승인·폐기(`POST /problem-analysis/analysis/{id}/approve`·`/discard`) ·
 * 본문 되돌리기(`POST /problem-content/problems/{id}/revert`) ·
 * 변형 승인·반려(`POST /problem-variation/variations/{id}/approve`·`/reject`) ·
 * 생성 승인·반려(`POST /problem-generation/generations/{id}/approve`·`/reject`).
 * 배치 폐기(`POST /problem-variation/batches/{id}/discard`, BE-20261102 회신 §1)도 문항 반려와 같은 게이트라 이 축이다.
 *
 * AI_RUN_ROLES 와 값이 같은데도 따로 두는 이유: 뜻이 다른 축이다. AI 실행은 오너가 교사에게 열 예정이고,
 * 그날 이 상수를 재사용해 뒀다면 승인 버튼까지 함께 열린다. 하나로 합치면 한쪽만 여는 날 못 연다
 * (OCR_ROLES 를 V2_ROLES 와 따로 둔 것과 같은 이유다).
 *
 * 그리고 여기서 막는 것은 「비용」이 아니라 **되돌리기 비용**이다 — 잘못 승인된 문제는 문제은행에 들어가
 * 그 뒤 만드는 모든 시험지에 실린다. AI 실행은 한 번 더 돌리면 되지만 확정은 그렇지 않다.
 *
 * ⚠ 「되돌리기」가 세 종류인데 403 은 **문제 본문** 하나뿐이다. 시험지 되돌리기(`POST /papers/{id}/revert`)와
 * 그림 되돌리기는 교사에게 열려 있다(§1-1) — 이름만 보고 이 축에 묶지 마라.
 */
export const BANK_APPROVE_ROLES = ['manager'];

/**
 * **배정 삭제**(`DELETE /exam-papers/assignments/{aid}`)를 할 수 있는 유형 — 매니저만.
 *
 * 채점 기록까지 함께 지우는 비가역 삭제라 서버가 교사에게 403 으로 남겼다(회신 §1-2·§5-③).
 * 배정 만들기·채점·마감·재오픈은 교사에게 열려 있다 — **지우는 것 하나만** 다르다.
 * 위 둘과 합치지 않는 이유도 같다: 비용의 축도, 은행 확정의 축도 아닌 제 축이다.
 *
 * 값이 하나뿐이어도 배열인 이유는 위와 같다 — 새 유형은 여기에 이름을 올려야만 통과한다.
 */
export const ASSIGN_DELETE_ROLES = ['manager'];

/**
 * **편집기 안 그림을 눌렀을 때 뜨는 버튼 넷**(편집 · AI 요청 · 이미지 교체 · 지우기)을 쓸 수 있는 유형 — 매니저만.
 * 강사에게는 **크기 조절 손잡이만** 남는다(오너 지시 2026-09-10 「이미지를 선택했을 때 버튼들 모두 가려줘 · 크기만 조절 가능하게」).
 *
 * 발단은 넷 중 [AI 요청] 하나였다 — LLM 비용이 나는데 2026-09-09 감추기에서 빠져 있었다. 오너가 하나만 빼는 대신
 * 넷을 통째로 닫기로 했다. 그래서 AI_RUN_ROLES 와 값이 같아도 합치지 않는다: 그쪽은 「비용」의 축이라 오너가
 * 강사에게 열 예정이고, 그날 이 넷(지우기·교체 같은 서버 쓰기 포함)까지 함께 열려야 하는지는 따로 정할 일이다.
 *
 * 서버는 이 경로들을 강사에게 열어 뒀다(BE-20260910 회신 §1-1 「그림 편집 전부」) — 화면에서만 감춘다.
 * 감추는 방식은 FigureEditContext 의 규약을 따른다: `onAction` 을 주지 않으면 편집기가 버튼을 그리지 않는다
 * (붙여넣기 판정 `onPasteImage` 는 그대로 준다 — 용량·형식 검사와 「N번째 바꾸기」 창은 버튼과 무관하다).
 *
 * 값이 하나뿐이어도 배열인 이유는 위와 같다 — 새 유형은 여기에 이름을 올려야만 통과한다.
 */
export const FIGURE_EDIT_ROLES = ['manager'];

/**
 * **학생 v2 전환 깃발** — false: 운영 전환 전(운영 빌드의 학생 v2 메뉴 · /academy 주소 닫힘, v1 학생관리 · 학습분석의 학교·학년·강의 연필 유지) /
 * true: 전환일(백엔드 스위치 v1_school_lock · v1_writeback 을 켜는 날). 전환일에는 이 한 줄만 true 로 바꿔 prod 로 올린다
 * (백엔드 전환 깃발 전달 docs/student-admin-v2/backend/BE-20261014-cutover-flag.md).
 * 로컬 개발의 메뉴 접근은 아래 ACADEMY_ROLES 에서 별도로 허용한다. 이 값과 백엔드 스위치는 바꾸지 않는다.
 */
export const ACADEMY_CUTOVER = false;

/**
 * 표준 SVG 그림 편집기 — 로컬 개발과 dev·운영 배포본에서 모두 켠다.
 * 문제 v2와 OCR 검수가 같은 설정을 사용한다.
 */
export const FIGURE_SVG_EDITOR = true;

/**
 * **학생 v2(/academy)** 메뉴와 화면에 들어올 수 있는 유형 — 로컬 개발(npm start) 또는 운영 전환 뒤에는 매니저 · 강사.
 * 전환 전 운영 빌드(npm run build)는 빈 배열로 Sidebar 메뉴와 App 주소 가드를 함께 닫는다.
 * CRA의 NODE_ENV는 start에서 development, build에서 production으로 고정되므로 로컬 개발 허용이 운영에 섞이지 않는다.
 *
 * 일괄 이관은 없다(결정 2-4). 강사에게는 8단계에 열었다(백엔드 8단계 전달 §4-A) — 강사 범위는 서버가 가른다
 * (오늘 기준 v1 담당 학생 ∪ 오늘 담임인 반 학생). 출결(7단계)과 담임 부재 예정(직원 일정)은 아직 「준비 중」이다(stageGate).
 * 반 세팅은 아래 ACADEMY_SETUP_ROLES 가 따로 막는다.
 *
 * 설계: docs/student-admin-v2/1010_SPEC_student-admin-v2-screen.html §11-2 4번
 */
export const ACADEMY_ROLES = (ACADEMY_CUTOVER || process.env.NODE_ENV === 'development') ? ['manager', 'teacher'] : [];

/**
 * **반 세팅 · 직원 · 문의 · 설정 쓰기** — 반 만들기·수업 시간표·담임 지정·종강·학생 반 편성 등(오너 지시 2026-10-10
 * 「반 세팅과 관련된 기능은 매니저에게 있어야 됨. 수업, 시간 설정 등」). 서버도 강사에게 403 이다(요청서 §10).
 *
 * ACADEMY_ROLES 와 따로 두는 이유: 뜻이 다른 축이다. ACADEMY_ROLES 에 강사를 더할 때(8단계) 이 값을 재사용해 뒀다면
 * 반 세팅까지 함께 열렸다(OCR_ROLES 를 V2_ROLES 와 따로 둔 것과 같은 이유).
 */
export const ACADEMY_SETUP_ROLES = ['manager'];
